<template>
    <ValidationObserver ref="form" v-slot="{ invalid }">
        <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="loading"><spinner line-fg-color="#922C49" size="55" :line-size="5"></spinner></div>
        <form id="form" v-if="!loading" @submit.prevent="submitForm" class="flex flex-col h-full" autocomplete="off">
            <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
                <template slot="title">{{ type }} Communication</template>
            </page-header>

            <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" role="alert" v-show="invalid && submitted" >Please complete all required fields</div>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" v-if="errorMessage">{{ errorMessage }}</div>

            <div class="flex md:flex-row sm:flex-col flex-grow">
                <div class="md:w-3/4 sm:w-full px-4 sm:px-6 lg:px-6 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'details'">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-text class="col-span-8"
                                label="Subject"
                                name="subject"
                                placeholder="Subject"
                                v-model="item.subject"
                                rules="required"
                                :required="true"
                            />
                            <input-select class="col-span-4"
                                label="Type"
                                name="type_id"
                                placeholder="Type"
                                v-model="item.type_id"
                                return="value"
                                :options="options.communicationTypes"
                                :initialValue="initialValue.communication_type"
                            />
                            <!-- Options -->
                            <input-textarea v-if="typeName === 'SMS'"
                                class="col-span-12"
                                label="Message"
                                name="sms_content"
                                placeholder="Message"
                                ref="content_input"
                                v-model="item.body"
                                rows="6"
                            />
                            <div v-if="typeName === 'SMS'" class="sm:col-span-12 flex flex-wrap text-gray-700 -mt-4">
                                <div class="py-2 mr-2 text-sm">
                                    Codes
                                </div>
                                <div @click="insertCode(qrLinkTag)" class="uppercase border border-gray-500 p-1 mr-2 place-self-center whitespace-no-wrap text-center text-xs cursor-pointer">
                                    QR Link
                                </div>
                            </div>
                            <div class="w-full block col-span-12 -mt-3">
                                <small :class="{'text-red-600': contentLength > 918}">Characters: {{ contentLength }} / 918</small>
                            </div>
                        </div>
                    </ValidationObserver>
                </div>
                <div class="md:w-3/4 sm:w-full px-4 sm:px-6 lg:px-6 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'recipients'">
                    <h2 class="w-full col-span-12 pb-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900 mt-4">
                        Recipients
                    </h2>
                    <div v-if="!membersLoading">
                        <table class="min-w-full text-xs">
                            <thead>
                                <tr>
                                    <th class="table-head-cell pl-0">Name</th>
                                    <th class="table-head-cell pl-0">Type</th>
                                    <th class="table-head-cell pl-0">DOB</th>
                                    <th class="table-head-cell pl-0">Mobile</th>
                                    <th class="table-head-cell pl-0 text-center">Age</th>
                                </tr>
                            </thead>
                            <tbody v-if="recipients.length">
                                <tr v-for="(item, index) in recipients" v-bind:key="index">
                                    <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.first }} {{ item.last }}</td>
                                    <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.type ? item.type.name : '' }}</td>
                                    <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.date_of_birth }}</td>
                                    <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.type !== 'Member' ? (item.parent ? item.parent.mobile : item.mobile) : item.mobile }}</td>
                                    <td class="table-body-cell pl-0 text-center" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.age }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                </div>
                <div class="md:w-1/4 sm:w-full px-4 sm:px-6 lg:px-6">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-4 grid row-gap-2 col-gap-4 grid-cols-12">
                            <h2 class="w-full col-span-12 pb-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">
                                Recipient Filters
                            </h2>
                            <input-select class="col-span-12"
                                label="Application Form"
                                name="application_form_id"
                                placeholder="Application Form"
                                v-model="item.filters.application_form_id"
                                return="value"
                                :options="options.forms"
                                :initialValue="item.filters.application_form_id"
                                @input="applicationFormUpdated()"
                            />
                            <input-select v-if="selectedFormHasQr"
                                class="col-span-12"
                                label="Scanned In"
                                name="qr_scan"
                                placeholder="Scanned In"
                                v-model="item.filters.qr_scan"
                                return="value"
                                :options="options.qrScan"
                                :initialValue="item.filters.qr_scan || 'all'"
                                @input="filterUpdated()"
                            />
                            <input-select class="col-span-12"
                                label="Application Status"
                                name="application_status_id"
                                placeholder="Application Status"
                                v-model="item.filters.application_status_id"
                                return="value"
                                :options="options.applicationStatuses"
                                :initialValue="item.filters.application_status_id"
                                @input="filterUpdated()"
                            />
                            <input-select class="col-span-12"
                                label="FAS Status"
                                name="tap_status_id"
                                placeholder="FAS Status"
                                v-model="item.filters.tap_status_id"
                                return="value"
                                :options="options.tapStatuses"
                                :initialValue="item.filters.tap_status_id"
                                @input="filterUpdated()"
                            />
                            <input-select class="col-span-12"
                                label="Supplier Status"
                                name="supplier_status_id"
                                placeholder="Supplier Status"
                                v-model="item.filters.supplier_status_id"
                                return="value"
                                :options="options.supplierStatuses"
                                :initialValue="item.filters.supplier_status_id"
                                @input="filterUpdated()"
                            />
                            <input-select class="col-span-12"
                                label="Application Used"
                                name="used"
                                placeholder="Application Used"
                                v-model="item.filters.used"
                                return="value"
                                :options="options.used"
                                :initialValue="item.filters.used"
                                @input="filterUpdated()"
                            />
                            <input-select class="col-span-12"
                                label="Member Type"
                                name="member_type_id"
                                placeholder="Member Type"
                                v-model="item.filters.member_type_id"
                                return="value"
                                :options="options.memberTypes"
                                :initialValue="item.filters.member_type_id"
                                @input="filterUpdated()"
                            />
                            <input-toggle class="sm:col-span-12"
                                label="Send to Member (if Spouse/Dependant)"
                                name="send_to_member"
                                v-model="item.filters.send_to_member"
                                @input="filterUpdated()"
                            />
                        </div>
                    </ValidationObserver>
                </div>
            </div>
            <transition name="fade">
                <confirmation-modal v-if="showConfirmModal" @close="showConfirmModal=false" @confirm="submitForm" ref="confirmModal">
                    <template slot="content">There are No recipients added, would you like to save?</template>
                </confirmation-modal>
            </transition>
        </form>
    </ValidationObserver>
</template>

<script>
    import FormSetup from "../../mixins/Tabs";
    import FormButtons from "../../mixins/FormButtons";
    import ConfirmationModal from "../../components/modals/ConfirmationModal";
    import axios from "axios";

    export default {
        name: "TemplateForm",
        props: {
            id: {required: false},
            type: {required: true},
            return: {required: false, default: '/communications'},
            page: {required: false, default: 'communications'}
        },
        components: {
            ConfirmationModal
        },
        mixins: [
            FormSetup,
            FormButtons
        ],
        data: function () {
            return {
                resource: 'communications',
                pageActions: {
                    buttons: [
                        {
                            label: 'Save & Close',
                            icon: 'save',
                            iconClass: 'text-green-600',
                            type: 'method',
                            method: 'save',
                        },
                        {
                            label: 'Send',
                            icon: 'paper-plane',
                            iconClass: 'text-default-600',
                            type: 'method',
                            method: 'send',
                            display: this.$store.getters.permissions.indexOf('custom.send-communication') !== -1
                        },
                        {
                            label: 'Cancel',
                            icon: 'times',
                            iconClass: 'text-red-600',
                            type: 'method',
                            method: 'cancel',
                        }
                    ]
                },
                startTab: null,
                loading: true,
                submitted: false,
                errorMessage: '',
                item: {
                    filters: {
                        application_form_id: null,
                        qr_scan: null,
                        used: null,
                        member_type_id: null,
                        application_status_id: null,
                        tap_status_id: null,
                        supplier_status_id: null,
                        send_to_member: false
                    }
                },
                S3URL: process.env.VUE_APP_S3_URL,
                options: {
                    communicationTypes: [],
                    memberTypes: [],
                    applicationStatuses: [],
                    tapStatuses: [],
                    supplierStatuses: [],
                    used: [
                        {
                            label: 'Yes',
                            value: true
                        },
                        {
                            label: 'No',
                            value: false
                        }
                    ],
                    forms: [],
                    qrScan: [
                        {
                            label: 'All',
                            value: 'all'
                        },
                        {
                            label: 'Scanned In Only',
                            value: 'scanned'
                        },
                        {
                            label: 'Not Scanned Only',
                            value: 'not_scanned'
                        }
                    ]
                },
                qrLinkTag: '{{qr-link}}',
                sendOnSave: false,
                membersLoading: false,
                showConfirmModal: false,
                recipients: []
            }
        },
        methods: {
            getData: function () {
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/communications/' + this.id, )
                    .then(response => {
                        this.item = response.data;

                        if (this.item.sent_at){
                            this.$router.push('/communications/'+this.item.id+'/view');
                        }

                        this.$Progress.finish();
                        this.loading = false;
                    })
                    .catch(() => {
                        this.$Progress.finish();
                        this.loading = false;
                    });
            },
            send: function() {
                if(!this.recipients || this.recipients.length === 0){
                    this.showConfirmModal = true;
                } else {
                    this.submitForm(true);
                }
            },
            submitForm: function (send = false) {
                this.submitted = false;

                this.$refs.form.validate().then(success => {
                    let _method;
                    let _url;

                    if (!success) {
                        this.submitted = true;
                        return;
                    }

                    this.submitted = true;

                    this.$store.commit('TOGGLE_BACKDROP', {open: true});
                    this.errorMessage = '';

                    if (this.id) {
                        _url = process.env.VUE_APP_API_URL + '/communications/' + this.id;
                        _method = 'PUT';
                    } else if (this.item.id) {
                        _url = process.env.VUE_APP_API_URL + '/communications/' + this.item.id;
                        _method = 'PUT';
                    } else {
                        _url = process.env.VUE_APP_API_URL + '/communications';
                        _method = 'POST';
                    }

                    this.item.send = send;

                    axios({
                        method: _method,
                        url: _url,
                        data: this.item
                    }).then(response => {
                        if (response.data.success) {
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});
                            this.$router.push('/communications');

                            this.$notify({
                                group: 'main',
                                type: 'success',
                                title: 'Success',
                                text: response.data.message,
                                duration: 5000
                            });

                        } else {
                            if (response.data.message) {
                                this.errorMessage = response.data.message;
                            }
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});
                        }
                    }).catch(error => {
                        if (error.response.data.message) {
                            this.errorMessage = error.response.data.message;
                        } else {
                            this.errorMessage = 'An error occurred. Please contact the System Administrator.';
                        }

                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                    });


                });
            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            },
            insertCode(code){
                this.$nextTick(() => {
                    let textarea = this.$refs.content_input
                    let cursorPosition = textarea.$refs.sms_content.selectionStart

                    const content = textarea.$refs.sms_content.value

                    if (content && content !== '') {
                        const textBefore = content.substring(0, cursorPosition)
                        const textAfter = content.substring(cursorPosition, content.length)

                        textarea.$refs.sms_content.value = textBefore + code + ' ' + textAfter
                        this.item.body = textBefore + code + ' ' + textAfter
                    } else {
                        textarea.$refs.sms_content.value = code + ' '
                        this.item.body = code + ' '
                    }

                    textarea.$refs.sms_content.focus()
                    textarea.$refs.sms_content.blur()
                    textarea.$refs.sms_content.focus()
                })
            },
            setFormOptions() {
                this.options.forms = this.$store.getters.forms.map(function (form) {
                    return { label: form.name, value: form.id, qr_code: !!form.qr_code };
                });
            },
            applicationFormUpdated() {
                if (!this.item.filters) {
                    return
                }

                // The scan filter only exists for forms that issue QR codes.
                if (!this.selectedFormHasQr) {
                    this.$set(this.item.filters, 'qr_scan', null)
                } else if (!this.item.filters.qr_scan) {
                    this.$set(this.item.filters, 'qr_scan', 'all')
                }

                this.filterUpdated()
            },
            filterUpdated(){
                this.membersLoading = true;
                this.recipients = [];

                axios.get(process.env.VUE_APP_API_URL + '/communications/recipients', {
                    params: this.item.filters
                }).then(response => {
                    this.recipients = response.data;

                    this.$Progress.finish();
                    this.membersLoading = false;
                }).catch(() => {
                    this.$Progress.finish();
                    this.membersLoading = false;
                });

            }
        },
        beforeMount() {
            // Communication Types
            if (this.$store.getters.communicationTypes.length === 0){
                this.$store.dispatch('GET_COMMUNICATION_TYPES').then(() => {
                    this.options.communicationTypes = this.$store.getters.communicationTypes.map(function (type) {
                        return { value: type.id, label: type.name };
                    });
                });
            } else {
                this.options.communicationTypes = this.$store.getters.communicationTypes.map(function (type) {
                    return { value: type.id, label: type.name };
                });
            }

            // Cached lists from before QR codes omit the flag, so refresh those.
            const cachedForms = this.$store.getters.forms
            const formsIncludeQr = cachedForms.length > 0 && Object.prototype.hasOwnProperty.call(cachedForms[0], 'qr_code')

            if (cachedForms.length > 0) {
                this.setFormOptions()
            }

            if (!formsIncludeQr) {
                this.$store.dispatch('GET_APPLICATION_FORMS').then(() => {
                    this.setFormOptions()
                });
            }

            // Member Types
            if (this.$store.getters.memberTypes.length === 0){
                this.$store.dispatch('GET_MEMBER_TYPES').then(() => {
                    this.options.memberTypes = this.$store.getters.memberTypes.map(function (form) {
                        return { label: form.name, value: form.id };
                    });
                });
            } else {
                this.options.memberTypes = this.$store.getters.memberTypes.map(function (form) {
                    return { label: form.name, value: form.id };
                });
            }

            // Application Statuses
            if (this.$store.getters.applicationStatuses.length === 0){
                this.$store.dispatch('GET_APPLICATION_STATUSES').then(() => {
                    this.options.applicationStatuses = this.$store.getters.applicationStatuses.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.options.applicationStatuses = this.$store.getters.applicationStatuses.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }

            // TAP Statuses
            if (this.$store.getters.fasApplicationStatuses.length === 0){
                this.$store.dispatch('GET_FAS_APPLICATION_STATUSES').then(() => {
                    this.options.tapStatuses = this.$store.getters.fasApplicationStatuses.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.options.tapStatuses = this.$store.getters.fasApplicationStatuses.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }

            // Supplier Statuses
            if (this.$store.getters.supplierStatuses.length === 0){
                this.$store.dispatch('GET_SUPPLIER_STATUSES').then(() => {
                    this.options.supplierStatuses = this.$store.getters.supplierStatuses.map(function (type) {
                        return { label: type.name, value: type.id };
                    });
                });
            } else {
                this.options.supplierStatuses = this.$store.getters.supplierStatuses.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            }
        },
        mounted() {
            if (this.type === "New") {
                this.loading = false;
                this.$Progress.finish();

                this.item.filters = {
                    application_form_id: null,
                    qr_scan: null,
                    used: null,
                    member_type_id: null,
                    application_status: null,
                    tap_status: null,
                    supplier_status: null,
                    send_to_member: false
                }
                // Se defaults here
            } else if (this.type === "Edit") {
                this.getData();
            }

            let windowLocationHash = window.location.hash.replace('#', '');

            if (windowLocationHash) {
                this.startTab = windowLocationHash;
            }
        },
        computed: {
            initialValue: function(){
                let output = {};

                if (this.type === "New" && this.$store.getters.communicationTypes && this.$store.getters.communicationTypes.length > 0){
                    output['communication_type'] = this.$store.getters.communicationTypes.filter(type => {
                        if (type.name === 'SMS'){
                            return type.id;
                        } else {
                            return false;
                        }
                    })[0].id;
                } else {
                    output['communication_type'] =  null
                }

                return output;
            },
            typeName: function(){
                if (this.item.type_id) {
                    return this.options.communicationTypes.find(type => {
                        return type.value === this.item.type_id
                    }).label
                }

                return null
            },
            tabs: function(){
                return [
                    {
                        name: 'Details',
                        slug: 'details'
                    },
                    {
                        name: `Recipients (${this.membersLoading ? 'Loading...' : this.recipients.length})`,
                        slug: 'recipients'
                    }
                ]
            },
            contentLength: function () {
                if (this.item?.body) {
                    return this.item.body.length;
                }

                return 0;
            },
            selectedFormHasQr: function () {
                const formId = this.item.filters && this.item.filters.application_form_id

                if (!formId || !this.options.forms) {
                    return false
                }

                const form = this.options.forms.find(option => option.value === formId)

                return !!(form && form.qr_code)
            },
        }
    }
</script>

<style scoped>

</style>
